/**
 * SonidosVigilante — los sonidos del flujo del día, EN TODA LA SESIÓN.
 *
 * Antes la detección vivía en la página de la Agenda y solo sonaba con ella
 * abierta; los anestesistas pueden estar en la hoja de anestesia, recepción
 * en Cobros… (2026-08-20, dirección: «tiene que ser en toda la sesión»). Este
 * componente vive en AppShell —el marco de cualquier pantalla— y sondea un
 * endpoint ligero cada 25 s.
 *
 * Reglas (cada rol oye SOLO lo suyo, y nunca en la primera carga):
 *  · visto de enfermería aparece  → alarma, solo a quien tenga
 *    `alarma_visto_enfermeria` en su ficha (hoy, dirección);
 *  · quirófano pasa a «Realizada» (= en recuperación) → pitido a
 *    administradores y administrativas; el chalazión de quirófano, al
 *    pasar a «Realizado» (el mismo estado); «No realizado» no suena
 *    (30/9/2026); una consulta citada en quirófano (una revisión) tampoco
 *    suena al pasar a «Realizada»: el servidor no la marca `quirofano`
 *    (2/10/2026);
 *  · pasa a «Listo para cambiarse» → soniquete largo a enfermería y
 *    anestesistas.
 */
import { useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { sonarAlarma, sonarLlegada, sonarPitido, sonarSoniquete } from '../lib/sonidos';

const INTERVALO_MS = 25_000;

export default function SonidosVigilante() {
  const { user } = useAuth();
  const previoRef = useRef(null);   // Map id → {estado, visto} de la última pasada

  useEffect(() => {
    if (!user?.id) return undefined;
    const rol = (user.role || '').toLowerCase();
    if (rol === 'tablet' || rol === 'tablet_consent') return undefined;

    let vivo = true;
    const pasada = async () => {
      let citas;
      try {
        const r = await api.get('/api/citas/sonidos-hoy');
        citas = r.data?.citas;
      } catch {
        return;                                  // sin red ahora mismo: silencio
      }
      if (!vivo || !Array.isArray(citas)) return;
      const ahora = new Map(citas.map((c) => [c.id, c]));
      const antes = previoRef.current;
      previoRef.current = ahora;
      if (antes === null) return;                // primera carga: silencio

      let alarma = false;
      let pitido = false;
      let soniquete = false;
      let llegada = false;
      for (const c of citas) {
        const p = antes.get(c.id);
        if (!p) continue;                        // cita nueva del día: no es un cambio de estado
        if (!p.visto && c.visto) alarma = true;
        if (p.estado !== c.estado) {
          if (c.estado === 'realizada' && c.quirofano) pitido = true;
          if (c.estado === 'postop_ok') soniquete = true;
          // Paciente DE ENFERMERÍA (previa, 1ª revisión, hialuronidasa…)
          // marcado en sala de espera → aviso a enfermería (2026-08-20).
          // Qué agendas cuentan lo decide el servidor (24/9/2026: la de
          // Enfermería y las de consulta conjunta, no Rinoplastia).
          if (c.estado === 'espera' && c.enfermeria) llegada = true;
        }
      }
      if (alarma && user.alarma_visto_enfermeria) sonarAlarma();
      if (pitido && (rol === 'admin' || rol === 'direccion' || rol === 'administrativo')) sonarPitido();
      if (soniquete && (rol === 'enfermeria' || rol === 'anestesista')) sonarSoniquete();
      if (llegada && rol === 'enfermeria') sonarLlegada();
    };

    pasada();
    const id = setInterval(pasada, INTERVALO_MS);
    return () => { vivo = false; clearInterval(id); };
  }, [user]);

  return null;
}
